import React, { useCallback, useContext, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import { BillwerkContext } from '@Domains/Checkout/Providers/BillwerkContextProvider'
import { CheckoutModalContext } from '@Domains/Checkout/Providers/CheckoutModalProvider'
import { PaymentError } from '@Domains/Checkout/Steps/Payment/Components/PaymentError/PaymentError'
import { PaymentResponseCallback } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import {
  useDefaultCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Default/hooks/useDefaultCheckoutResponse'
import { useTranslation } from '@Globals/Translations'
import { CheckoutV2FinalizeCartResult } from '@Pages/onestepcheckout'

export const useBillwerkCheckoutResponse = () => {
  const { billwerkInstance } = useContext(BillwerkContext)
  const { setModal, unSetModal } = useContext(CheckoutModalContext)
  const { handleErrorResponse, handleRedirect } = useDefaultCheckoutResponse()
  const { t } = useTranslation()

  const hasBillwerkError = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    const response = finalizeCart.paymentPayload as any
    if (response?.resultCode) {
      return !!response.paymentPayload?.success
    }
    return !finalizeCart.success
  }, [])

  const handleBillwerkError = useCallback((finalizeCart: CheckoutV2FinalizeCartResult, paymentType: string) => {
    if (hasBillwerkError(finalizeCart)) {
      ampli.cofCompletionFailRes({
        payment_method: paymentType,
      })
      setModal(<PaymentError closeModal={unSetModal} error={t('checkout:COF_PAYMENT_CARD_ERROR_DEFAULT')} />)
      return true
    }
    return false
  }, [hasBillwerkError, setModal, t, unSetModal])

  const handleBillwerkRedirectFromAction = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    const response = finalizeCart.paymentPayload as any
    const redirectShopper = !!response?.success?.id
    if (redirectShopper) {
      // eslint-disable-next-line no-new
      new billwerkInstance.WindowCheckout(response?.success?.id)
      return true
    }
    return false
  }, [billwerkInstance])

  const handleBillwerkResponse: PaymentResponseCallback = useCallback((paymentType: string, finalizeCart: CheckoutV2FinalizeCartResult) => {
    let res: boolean = handleBillwerkError(finalizeCart, paymentType)
    res = res || handleBillwerkRedirectFromAction(finalizeCart)
    res = res || handleErrorResponse(finalizeCart, paymentType)
    res = res || handleRedirect(finalizeCart)
    return res
  }, [handleBillwerkRedirectFromAction, handleBillwerkError, handleErrorResponse, handleRedirect])

  const value = useMemo(() => ({ handleBillwerkResponse }), [handleBillwerkResponse])

  return value
}
